import React, { useEffect, useLayoutEffect, useState } from 'react';
import { BrowserRouter as Router, Switch } from 'react-router-dom';
import PrivateRoute from './routes/PrivateRoute';
import './apis/api';
import {
  AggregateDispatchView,
  AggregatePlanView,
  ChassisDropView,
  DayWiseAgsnView,
  AvailabilityView,
  EmergencyAgsnView,
  LandingPage,
  PlanningView,
  ProductionReviewPage,
  MonthlyPlanningView,
} from './containers';
import { InformationRadiator, ShiftConfigurator } from './components';
import {
  AGGREGATES,
  LINE_TYPE,
  RESOURCE_TYPE,
  USER_OPERATIONS,
  APPLICATION_VARIANTS,
  JLR_PLANT,
  APP_TITLE,
} from './constants';

import {
  getAggregatePlanUrl,
  getAggregateUrl,
  getAggregateSequenceUrl,
  getAggregateReSequenceUrl,
  getAggregateLineUpUrl,
  getEmergencyAgSNUrl,
  getAggregateDispatchUrl,
  getAggregateAvailabilityUrl,
  getAgSNOverviewUrl
} from './utils/utils';
import { overrideConsole } from './hooks/loggingErrors';
import { useErrorHandler } from 'react-error-boundary';
import { useAuthorizationContext } from './components/AuthorizationProvider/AuthorizationProvider';
import AsnUpdatesLive from './components/AsnUpdatesLive/AsnUpdatesLive';
import { ExcelResequence, Resequence, SequencePreviews } from './pages';
import HeaderAndFooter from './layouts/HeaderAndFooter/HeaderAndFooter';
import AggregateSequence from './components/AggregateSequence/AggregateSequence';
import AggregateResequenceView from './components/AggregateResequenceView/AggregateResequenceView';
import { usePlantAndLinesStore } from './providers/PlantAndLinesStore/PlantAndLinesStore';
import { shopType } from './staticData';
import { fetchLinesForGivenPlantAndPurpose } from './apis/calls';
import config from '../src/config/config';
import AsnOverviewReportView from './containers/AsnOverviewReportView/AsnOverviewReportView';
import AvailabilityTabView from './containers/AvailabilityTabView/AvailabilityTabView';
import AgsnUpdatesLive from './components/AgsnUpdatesLive/AgsnUpdatesLive';
import EvrNumberConfigurator from './components/EvrNumberConfigurator/EvrNumberConfigurator';
import EngineConfirmationUpdate from './components/EngineConfirmationUpdate/EngineConfirmationUpdate';
import PBSDropping from './components/PBSDropping/PBSDropping';

function AggregateLines({ shopType: shop, Component, props }) {
  const INITIAL_LINE = { value: null, label: '' };

  const [plant] = usePlantAndLinesStore().plantState;
  const setAggregateLine = usePlantAndLinesStore().aggregateLineState[1];
  const setAggregateLineOptions = usePlantAndLinesStore()
    .aggregateLineOptions[1];

  const [loading, setLoading] = useState(null);
  const [line, setLine] = useState(INITIAL_LINE);
  const [lineOptions, setLineOptions] = useState([]);

  const [aggregateLine] = usePlantAndLinesStore().aggregateLineState;

  const loadAggregateLines = async (plantId, purpose) => {
    setLoading(true);
    setLine(INITIAL_LINE);
    const { data } = await fetchLinesForGivenPlantAndPurpose(plantId, purpose);

    const mainAggregateLines = data
      .filter(line => line.lineType === LINE_TYPE.MAIN)
      .map(record => {
        return { value: record.line, label: record.lineName };
      });

    const lines = mainAggregateLines.map(record => record.value);
    if (lines?.includes(aggregateLine?.value)) {
      setLine(aggregateLine);
      setLineOptions(mainAggregateLines);
      setLoading(false);
    } else {
      setLine(mainAggregateLines[0] || INITIAL_LINE);
      setLineOptions(mainAggregateLines);
      setLoading(false);
    }
  };

  useLayoutEffect(() => {
    if (plant.value) {
      loadAggregateLines(plant.value, shop);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value]);

  useEffect(() => {
    if (plant.value && !loading) {
      setAggregateLine(line);
      setAggregateLineOptions(lineOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value, loading, line, lineOptions]);

  return loading === false && <Component {...props} />;
}

function App() {
  const { isAuthenticated, user } = useAuthorizationContext();
  const handleError = useErrorHandler();
  const [plant] = usePlantAndLinesStore().plantState;
  const { APPLICATION_VARIANT } = config;
  const getFaviconEl = () => document.getElementById('favicon');

  const addLoggingHooks = () => {
    window.onerror = (message, source, lineno, colno, error) => {
      if (error.stack) {
        let sourceFunction = error.stack.split('\n')[1];
        handleError(new Error(message + sourceFunction));
      } else {
        handleError(error);
      }
    };
    var newConsole = overrideConsole(window.console, user?.userID);
    window.console = newConsole;
  };

  useEffect(() => {
    isAuthenticated && addLoggingHooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // to render favicon according to business unit
  useEffect(() => {
    if (
      APPLICATION_VARIANT === APPLICATION_VARIANTS.MES4 ||
      APPLICATION_VARIANT === APPLICATION_VARIANTS.MES4_EV
    ) {
      const favicon = getFaviconEl(); // Accessing favicon element
      if (plant.label === JLR_PLANT) {
        favicon.href = 'jlr-favicon.ico';
      } else {
        if (APPLICATION_VARIANT === APPLICATION_VARIANTS.MES4_EV) {
          favicon.href = 'mes4-ev-favicon.ico';
        } else {
          favicon.href = 'mes-favicon.ico';
        }
      }
    }
    if (APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4) {
      const favicon = getFaviconEl();
      favicon.href = 'favicon.ico';
    }
  }, [APPLICATION_VARIANT, plant]);

  // to show application title specific to CV and PV
  useEffect(() => {
    if (APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4) {
      document.title = APP_TITLE.IPMS4;
    } else if (
      APPLICATION_VARIANT === APPLICATION_VARIANTS.MES4 ||
      APPLICATION_VARIANT === APPLICATION_VARIANTS.MES4_EV
    ) {
      document.title = APP_TITLE.MES4;
    }
  }, [APPLICATION_VARIANT]);

  const isLucknowProlifePlant = plant?.label === 'Lucknow-Prolife'
  const pathName = isLucknowProlifePlant ? '/engine': '/chassis'

  return (
    <div className="App">
      <Router>
        <Switch>
          <PrivateRoute exact path="/planning">
            <HeaderAndFooter>
              <PlanningView />
            </HeaderAndFooter>
          </PrivateRoute>
          <PrivateRoute exact path={pathName}>
            <HeaderAndFooter>
              <ChassisDropView />
            </HeaderAndFooter>
          </PrivateRoute>
          <PrivateRoute exact path="/monthly-plan">
            <HeaderAndFooter>
              <MonthlyPlanningView />
            </HeaderAndFooter>
          </PrivateRoute>
          <PrivateRoute exact path="/">
            <HeaderAndFooter>
              <LandingPage />
            </HeaderAndFooter>
          </PrivateRoute>
          <PrivateRoute
            exact
            path="/operator"
            component={InformationRadiator}
          />
          <PrivateRoute exact path="/asn-live" component={AsnUpdatesLive} />
          <PrivateRoute exact path="/asn-overview-report">
            <HeaderAndFooter>
              <AsnOverviewReportView />
            </HeaderAndFooter>
          </PrivateRoute>
          <PrivateRoute
            exact
            path="/shift-configurator"
            operation={USER_OPERATIONS.GET_MASTER_SHIFT_CONFIGURATOR}
          >
            <HeaderAndFooter>
              <ShiftConfigurator />
            </HeaderAndFooter>
          </PrivateRoute>
          <PrivateRoute
            exact
            path="/production-review"
            resource={RESOURCE_TYPE.PRODUCTION_STATISTICS}
            operation={USER_OPERATIONS.GET_PRODUCTION_REVIEW}
          >
            <HeaderAndFooter>
              <ProductionReviewPage />
            </HeaderAndFooter>
          </PrivateRoute>
          <PrivateRoute
            exact
            path="/Engine-Confirmation"
            component={EngineConfirmationUpdate}
          />
          <PrivateRoute
            exact
            path="/evr-number-configurator"
            operation={USER_OPERATIONS.GET_ALL_EVR}
          >
            <HeaderAndFooter>
              <EvrNumberConfigurator />
            </HeaderAndFooter>
          </PrivateRoute>
          <PrivateRoute
            exact
            path="/pbs-dropping-confirmation"
            operation={USER_OPERATIONS.UPDATE_CHASSIS_PLAN_ASN}
          >
            <HeaderAndFooter>
              <PBSDropping />
            </HeaderAndFooter>
          </PrivateRoute>
          {Object.values(AGGREGATES).map(aggregate => (
            <PrivateRoute exact path={getAggregateUrl(aggregate)}>
              <HeaderAndFooter>
                <AvailabilityView aggregate={aggregate} />
              </HeaderAndFooter>
            </PrivateRoute>
          ))}
          {Object.values(AGGREGATES).map(aggregate => (
            <PrivateRoute exact path={getAggregatePlanUrl(aggregate)}>
              <HeaderAndFooter>
                <AggregateLines
                  shopType={shopType[aggregate]}
                  Component={AggregatePlanView}
                  props={{ aggregate }}
                />
              </HeaderAndFooter>
            </PrivateRoute>
          ))}
          {Object.values(AGGREGATES).map(aggregate => (
            <PrivateRoute
              exact
              path={`${getAggregateReSequenceUrl(aggregate)}/:sequenceDate`}
            >
              <HeaderAndFooter>
                <AggregateResequenceView aggregate={aggregate} />
              </HeaderAndFooter>
            </PrivateRoute>
          ))}
          {Object.values(AGGREGATES).map(aggregate => (
            <PrivateRoute
              exact
              path={`${getAggregateSequenceUrl(aggregate)}/:sequenceDate`}
            >
              <HeaderAndFooter>
                <AggregateSequence aggregate={aggregate} />
              </HeaderAndFooter>
            </PrivateRoute>
          ))}
          {Object.values(AGGREGATES).map(aggregate => (
            <PrivateRoute exact path={getEmergencyAgSNUrl(aggregate)}>
              <HeaderAndFooter>
                <AggregateLines
                  shopType={shopType[aggregate]}
                  Component={EmergencyAgsnView}
                  props={{ aggregate }}
                />
              </HeaderAndFooter>
            </PrivateRoute>
          ))}
          <PrivateRoute exact path="/sequence-previews">
            <HeaderAndFooter>
              <SequencePreviews />
            </HeaderAndFooter>
          </PrivateRoute>
          <PrivateRoute exact path="/resequence/:ISODate">
            <HeaderAndFooter>
              <Resequence />
            </HeaderAndFooter>
          </PrivateRoute>
          <PrivateRoute exact path="/excelresequence/:ISODate">
            <HeaderAndFooter>
              <ExcelResequence />
            </HeaderAndFooter>
          </PrivateRoute>
          {Object.values(AGGREGATES).map(aggregate => (
            <PrivateRoute exact path={getAggregateLineUpUrl(aggregate)}>
              <HeaderAndFooter>
                <AggregateLines
                  shopType={shopType[aggregate]}
                  Component={DayWiseAgsnView}
                  props={{ aggregate }}
                />
              </HeaderAndFooter>
            </PrivateRoute>
          ))}
          {Object.values(AGGREGATES).map(aggregate => (
            <PrivateRoute exact path={getAggregateDispatchUrl(aggregate)}>
              <HeaderAndFooter>
                <AggregateLines
                  shopType={shopType[aggregate]}
                  Component={AggregateDispatchView}
                  props={{ aggregate }}
                />
              </HeaderAndFooter>
            </PrivateRoute>
          ))}
          {Object.values(AGGREGATES).map(aggregate => (
            <PrivateRoute exact path={getAggregateAvailabilityUrl(aggregate)}>
              <HeaderAndFooter>
                <AggregateLines
                  shopType={shopType[aggregate]}
                  Component={AvailabilityTabView}
                  props={{ aggregate }}
                />
              </HeaderAndFooter>
            </PrivateRoute>
          ))}
          {Object.values(AGGREGATES).map(aggregate => (
            <PrivateRoute exact path={getAgSNOverviewUrl(aggregate)}>
              <AggregateLines
                shopType={shopType[aggregate]}
                Component={AgsnUpdatesLive}
                props={{ aggregate }}
              />
            </PrivateRoute>
          ))}
        </Switch>
      </Router>
    </div>
  );
}

export default App;
